/* eslint-disable react-hooks/exhaustive-deps */
/*
 *---------------------------------------------------
 * Licensed Materials - Property of HCL Technologies
 *
 * HCL Commerce
 *
 * (C) Copyright HCL Technologies Limited 2021
 *
 *---------------------------------------------------
 */
//Standard libraries
import { useEffect, ChangeEvent, MouseEvent, useMemo, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import Axios, { Canceler } from "axios";
//Foundation libraries
import { useSite } from "../../../../_foundation/hooks/useSite";
import { useBreadcrumbTrail } from "../../breadcrumb-trail-widget/hooks/use-breadcrumb-trail";
//Custom libraries
import getDisplayName from "react-display-name";
import { PRODUCT_LIST_FIELDS, PAGINATION_CONFIGS } from "../../../../configs/catalog";
import { SORT_OPTIONS } from "../../../../constants/catalog";
//Redux
import { keywordSelector } from "../../../../redux/selectors/search";
import {
  // catalogueURLSelector,
  promotionFacetSelector,
  selectedPageOffsetSelector,
  selectedPageNum,
} from "../../../../redux/selectors/catalog";
import {
  productListSelector,
  productListTotalSelector,
  priceModeSelector,
  selectedFacetsSelector,
  selectedFacetLimitsSelector,
  selectedFacetPricesSelector,
  selectedSortOptionSelector,
  selectFacetRemoveSelector,
  facetsSelector,
  breadcrumbsSelector,
} from "../../../../redux/selectors/catalog";
import * as CATALOG_ACTION_TYPES from "../../../../redux/action-types/catalog";
import { setProductListTotal, setPageLimit } from "../../../../redux/actions/plp";
import * as catalogActions from "../../../../redux/actions/catalog";
import { currentContractIdSelector } from "../../../../redux/selectors/contract";
import { SEARCH_PLP_PROFILE, SEARCH_FIND_PROFILE, GENERIC_CUSTOMER } from "../../../../constants/common";
import isEmpty from "lodash-es/isEmpty";
import { scrollToTop } from "../../../../utils/scrollToUtil";
import { useGenericPushWidget } from "../../generic-push-widget/generic-push-widget";
import { GetCategoriesSelector } from "../../../../redux/selectors/category";
import { useLocation } from "react-router-dom";

// ─── PLP snapshot + back-navigation helpers ──────────────────────────────────
const PLP_SNAPSHOT_KEY = "plp_snapshot";
const PLP_NAVIGATED_TO_PDP_KEY = "plp_navigated_to_pdp";
let _pendingRestore = false;
let _restoredForUrl = "";

interface PLPSnapshot {
  url: string; // pathname at save time (no query string needed for matching)
  products: any[]; // full accumulated product list (p1 + p2 + …)
  total: number; // productListTotal
  page: number; // highest page number loaded
  facets?: any[]; // facet sidebar list
  breadcrumbs?: any[]; // breadcrumb trail
}

// Called by ProductCard just before navigating to PDP
export const markNavigatingToPDP = (
  products?: any[],
  total?: number,
  page?: number,
  facets?: any[],
  breadcrumbs?: any[]
): void => {
  _pendingRestore = true;
  _restoredForUrl = "";
  try {
    sessionStorage.setItem(PLP_NAVIGATED_TO_PDP_KEY, "1");
    if (products && products.length > 0) {
      const snapshot = {
        url: `${window.location.pathname}${window.location.search}`,
        products,
        total: total ?? products.length,
        page: page ?? 1,
        facets: facets ?? [],
        breadcrumbs: breadcrumbs ?? [],
      };
      sessionStorage.setItem(PLP_SNAPSHOT_KEY, JSON.stringify(snapshot));
      console.log("[PLP-RESTORE] snapshot saved at click time:", products.length, "products, page:", page);
    }
    console.log("[PLP-RESTORE] markNavigatingToPDP — _pendingRestore=true");
  } catch (_) {
    // sessionStorage unavailable
  }
};

// Returns true and clears the flag when a PDP→PLP back-navigation is detected
const consumeBackFromPDPFlag = (): boolean => {
  const currentPathname = window.location.pathname;

  if (_pendingRestore) {
    const snapshot = loadSnapshot();
    if (snapshot && snapshot.url.startsWith(currentPathname)) {
      return true;
    }
    _pendingRestore = false;
    _restoredForUrl = "";
    clearSnapshot();
    return false;
  }

  try {
    const flag = sessionStorage.getItem(PLP_NAVIGATED_TO_PDP_KEY);
    if (flag === "1") {
      const snapshot = loadSnapshot();
      if (snapshot && snapshot.url.startsWith(currentPathname)) {
        _pendingRestore = true;
        return true;
      }
      clearSnapshot();
      return false;
    }
  } catch (_) {
    // sessionStorage unavailable
  }
  // Also check if we already restored for this URL (handles repeated mounts)
  if (_restoredForUrl && currentPathname === _restoredForUrl) {
    return true;
  }
  return false;
};

const saveSnapshot = (snapshot: PLPSnapshot): void => {
  try {
    sessionStorage.setItem(PLP_SNAPSHOT_KEY, JSON.stringify(snapshot));
  } catch (_) {
    // sessionStorage unavailable – fail silently
  }
};

const loadSnapshot = (): PLPSnapshot | null => {
  try {
    const raw = sessionStorage.getItem(PLP_SNAPSHOT_KEY);
    return raw ? (JSON.parse(raw) as PLPSnapshot) : null;
  } catch (_) {
    return null;
  }
};

const clearSnapshot = (): void => {
  // Also clear the restored-URL tracker so fresh navigations work normally
  _restoredForUrl = "";
  _pendingRestore = false;
  try {
    sessionStorage.removeItem(PLP_SNAPSHOT_KEY);
    sessionStorage.removeItem(PLP_NAVIGATED_TO_PDP_KEY);
    sessionStorage.removeItem("plp_navigated_via_pagination");
  } catch (_) {
    // sessionStorage unavailable – fail silently
  }
};

type SelectedFacet = {
  promotionEnabled?: boolean;
  selectedPageOffset?: number;
  minPrice?: number;
  maxPrice?: number;
  selectedSortOption?: number;
  pageNum?: number;
};

export const useProductGridLayout = (props: any) => {
  const categoryId = props.categoryId;
  const categoryIdentifier = props?.categoryIdentifier;
  const searchTerm = props.searchTerm;
  const { promotionComponent, productParameters } = props;
  const dispatch = useDispatch();
  const cancels: Canceler[] = [];
  const { mySite } = useSite();
  const categoriesRedux = useSelector(GetCategoriesSelector);
  const _productList = useSelector(productListSelector);
  const productListTotal = useSelector(productListTotalSelector);
  const priceMode = useSelector(priceModeSelector);
  const selectedFacetLimits = useSelector(selectedFacetLimitsSelector);
  const { breadcrumbs } = useBreadcrumbTrail();
  const selectedFacets = useSelector(selectedFacetsSelector);
  const selectedFacetPrices = useSelector(selectedFacetPricesSelector);
  const promotionFacetSelected = useSelector(promotionFacetSelector);
  const selectFacetRemove: boolean = useSelector(selectFacetRemoveSelector);
  const selectedMinPrice = selectedFacetPrices.min;
  const selectedMaxPrice = selectedFacetPrices.max;
  const selectedSortOption = useSelector(selectedSortOptionSelector);
  const defaultCurrencyID: string = mySite ? mySite.defaultCurrencyID : "";
  const pageDefaultOffset: number = PAGINATION_CONFIGS.pageDefaultOffset;
  const pageLimit: number = PAGINATION_CONFIGS.pageLimit;
  const contract = useSelector(currentContractIdSelector);
  const selectedPageOffset = useSelector(selectedPageOffsetSelector);
  const selectedPage = useSelector(selectedPageNum);
  const CancelToken = Axios.CancelToken;
  const priceSelected = selectedMinPrice > -1 && selectedMaxPrice > -1;
  const widgetName = getDisplayName("ProductGridLayout");
  const suggestedKeywords = useSelector(keywordSelector);
  const [categoryIdentifiers, setCategoryIdentifiers] = useState([]);

  const location = useLocation();

  // Consumed once on mount: true when user pressed Back from a PDP.
  // useRef keeps the value stable across re-renders.
  const isBackFromPDP = useRef<boolean>(consumeBackFromPDPFlag());

  // Guard so the restore path runs at most once per PLP mount.
  const snapshotRestored = useRef<boolean>(false);

  /**
   * Gets the list of default product sort options plus price sort options if price mode allows
   * @returns List of permitted sort options
   */
  const getSortOptions = () => {
    if (priceMode === "1") {
      const options = SORT_OPTIONS.getSortOptionsByIdentifier(
        isEmpty(searchTerm) ? SEARCH_PLP_PROFILE : SEARCH_FIND_PROFILE,
        mySite?.storeCfg?.userData?.SENSFUEL_RANKING_ENABLED === "true"
      );
      return options;
    }
  };
  const sortOptions = getSortOptions();

  const paramsBase: any = {
    currency: defaultCurrencyID,
    contractId: contract ? contract : "",
    _fields: PRODUCT_LIST_FIELDS,
    limit: pageLimit,
    offset: 0,
    widget: widgetName,
    cancelToken: new CancelToken(function executor(c) {
      cancels.push(c);
    }),
  };

  if (categoryId !== "") {
    paramsBase["categoryId"] = categoryId;
  }

  if (sortOptions?.[0]?.value !== selectedSortOption) {
    const orderBy = SORT_OPTIONS.getDefaultSortValue(
      isEmpty(searchTerm) ? SEARCH_PLP_PROFILE : SEARCH_FIND_PROFILE,
      mySite?.storeCfg?.userData?.SENSFUEL_RANKING_ENABLED === "true"
    );

    if (orderBy?.length > 0) {
      paramsBase["orderBy"] = orderBy;
    }
  }

  // if (selectedSortOption === "0" && paramsBase["orderBy"]) {
  //   delete paramsBase["orderBy"];
  // }

  if (searchTerm !== "") {
    paramsBase["searchTerm"] = searchTerm;
  }
  const productList = useMemo(() => {
    let rc = _productList;
    if (breadcrumbs?.length) {
      rc = _productList.map((p: any) => ({
        ...p,
        link: {
          pathname: p.seo?.href,
          state: {
            categoryId,
            breadCrumbTrailEntryView: [...breadcrumbs, { label: p.name, value: p.id }],
          },
        },
      }));
    }
    return rc;
  }, [breadcrumbs, _productList, categoryId]);

  useEffect(() => {
    if (categoriesRedux && categoryIdentifiers.length === 0) {
      const categorie = categoriesRedux.filter((c) => c.id === categoryId);
      const catIdentifiers: any = [];
      if (categorie.length > 0) {
        const parentCatalogGroupID = Array.isArray(categorie[0].parentCatalogGroupID)
          ? categorie[0].parentCatalogGroupID[0]
          : categorie[0].parentCatalogGroupID;
        parentCatalogGroupID?.split("/").forEach((id) => {
          const parentCategorie = categoriesRedux.filter((c) => c.id === id);
          if (parentCategorie.length > 0) {
            catIdentifiers.push(parentCategorie[0].identifier);
          }
        });
        setCategoryIdentifiers(catIdentifiers);
      }
    }
  }, [categoriesRedux]);

  const { genericPushWidget } = useGenericPushWidget(categoryIdentifiers);

  // const catalogueURL = useSelector(catalogueURLSelector);

  const isQueryParamAManualFilter = (key: string): boolean => {
    return ["promotionFacet", "minPrice", "maxPrice", "sortOption", "p"].includes(decodeURIComponent(key));
  };

  const isQueryParamAllowedAsFacet = (key: string): boolean => {
    const storeConfAllowedKeywords = mySite?.storeCfg?.userData?.ALLOWED_PLP_FACETS_KEYWORDS;
    const allowedFacetsKeyword = (storeConfAllowedKeywords && storeConfAllowedKeywords?.split(",")) || [
      "facets",
      "manufacturer",
    ];
    return allowedFacetsKeyword.some((facet: string) => key.includes(facet));
  };

  // ── Back-navigation restore ──────────────────────────────────────────────────
  useEffect(() => {
    const snapshot = loadSnapshot();
    // console.log("[PLP-RESTORE] mount check", {
    //   flag,
    //   isBackFromPDP: isBackFromPDP.current,
    //   snapshotExists: !!snapshot,
    //   snapshotUrl: snapshot?.url,
    //   currentPathname: window.location.pathname,
    //   snapshotProducts: snapshot?.products?.length,
    // });
    const currentPathname = window.location.pathname;

    if (!isBackFromPDP.current) {
      // console.log("[PLP-RESTORE] skipped — isBackFromPDP is false");
      return;
    }

    if (!snapshot || !snapshot.url.startsWith(currentPathname)) {
      isBackFromPDP.current = false;
      clearSnapshot();
      return;
    }

    snapshotRestored.current = true;
    // console.log("[PLP-RESTORE] restoring", snapshot.products.length, "products for page", snapshot.page);

    const queryPage = new URLSearchParams(window.location.search);
    const promotion = queryPage.get("promotionFacet");
    const minPrice = queryPage.get("minPrice");
    const maxPrice = queryPage.get("maxPrice");

    const restoredStates = {
      pageNum: snapshot.page,
      selectedPageOffset: (snapshot.page - 1) * pageLimit,
      selectedSortOption:
        queryPage.get("sortOption") ||
        SORT_OPTIONS.getDefaultSortValue(
          isEmpty(searchTerm) ? SEARCH_PLP_PROFILE : SEARCH_FIND_PROFILE,
          mySite?.storeCfg?.userData?.SENSFUEL_RANKING_ENABLED === "true"
        ),
      ...(promotion ? { promotionEnabled: true } : {}),
      ...(minPrice && maxPrice ? { minPrice: Number(minPrice), maxPrice: Number(maxPrice) } : {}),
    };

    // Dispatch isFetching=false FIRST to prevent PageLoader from showing,
    // which would unmount CatalogEntryListWidget and lose the restore state.
    dispatch({ type: CATALOG_ACTION_TYPES.PRODCUT_LIST_FETCHING, payload: false });

    dispatch({
      type: CATALOG_ACTION_TYPES.PRODUCT_LIST_GET_SUCCESS,
      response: {
        contents: snapshot.products,
        total: snapshot.total,
        facets: snapshot.facets || [],
        breadCrumbTrailEntryView: snapshot.breadcrumbs || [],
      },
      payload: {
        parameters: { ...paramsBase, identifier: categoryIdentifier },
        states: restoredStates,
        removeCommonSubCategories: !promotionComponent,
      },
    });

    dispatch(setProductListTotal(snapshot.total));
    dispatch(setPageLimit(genericPushWidget.count));

    _restoredForUrl = currentPathname;
    try {
      sessionStorage.removeItem(PLP_NAVIGATED_TO_PDP_KEY);
    } catch (_) {
      /* ignore */
    }
    console.log("[PLP-RESTORE] restore complete — _restoredForUrl set to", _restoredForUrl);
  }, []);

  useEffect(() => {
    if (!snapshotRestored.current) return;
    if (!isBackFromPDP.current) return;

    try {
      const anchorRaw = sessionStorage.getItem("plp_anchor");
      if (!anchorRaw) return;

      const anchor = JSON.parse(anchorRaw);
      const currentPLP = window.location.pathname + window.location.search;

      if (anchor.plpId !== currentPLP) {
        sessionStorage.removeItem("plp_anchor");
        return;
      }

      let attempts = 0;
      let restored = false;

      const interval = setInterval(() => {
        const el = document.querySelector(`[data-partnumber='${anchor.partNumber}']`);

        if (el) {
          const y = el.getBoundingClientRect().top + window.scrollY;

          window.scrollTo({
            top: y - 120,
            behavior: "auto",
          });

          restored = true;
        }

        attempts++;

        if (restored && attempts > 5) {
          clearInterval(interval);
          sessionStorage.removeItem("plp_anchor");
        }

        if (attempts > 50) {
          clearInterval(interval);
        }
      }, 300);
    } catch (err) {
      console.error(err);
    }
  }, [snapshotRestored.current]);
  useEffect(() => {
    if (
      mySite &&
      contract !== null &&
      (!isEmpty(categoryId) || !isEmpty(searchTerm)) &&
      (searchTerm !== "" || (searchTerm === "" && genericPushWidget.loading === true))
    ) {
      // Skip the API call if:
      const currentPathname = window.location.pathname;
      const shouldSkip = snapshotRestored.current || (_restoredForUrl !== "" && currentPathname === _restoredForUrl);
      console.log(
        "[PLP-RESTORE] main useEffect — skip:",
        shouldSkip,
        "snapshotRestored:",
        snapshotRestored.current,
        "_pendingRestore:",
        _pendingRestore,
        "_restoredForUrl:",
        _restoredForUrl
      );
      if (shouldSkip) {
        // Now safe to clear flags — main useEffect has been blocked
        _pendingRestore = false;
        return;
      }
      // Fresh navigation — clear any stale restored URL
      _restoredForUrl = "";
      _pendingRestore = false;

      const queryPage = new URLSearchParams(location.search);
      const page = parseInt(queryPage.get("p") || "0", 10);
      const promotion = queryPage.get("promotionFacet");
      const minPrice = queryPage.get("minPrice");
      const maxPrice = queryPage.get("maxPrice");

      const pageLimitWithoutPush = pageLimit - genericPushWidget.count;
      const pageOffset = page * pageLimitWithoutPush - pageLimitWithoutPush;

      const parameters = {
        ...paramsBase,
        offset: pageOffset > 0 ? pageOffset : 0,
        limit: pageLimit - genericPushWidget.count,
        identifier: categoryIdentifier,
      };
      const query: Record<any, any> = !isEmpty(selectedFacets) ? { facet: Object.keys(selectedFacets) } : {};
      // const states = !isEmpty(selectedFacets) ? { selectedFacets } : undefined;

      // let newStates: any = { ...states };
      // newStates = {
      //   ...newStates,
      //   selectedPageOffset: pageOffset,
      // };
      // if (page !== 0) {
      //   newStates["selectedPageOffset"] = pageOffset;
      // }

      Object.assign(query, {
        profileName: isEmpty(parameters.searchTerm) ? SEARCH_PLP_PROFILE : SEARCH_FIND_PROFILE,
      });
      Object.assign(query, {
        customerSegment: GENERIC_CUSTOMER,
      });
      const plpDisplaySkuCategories = mySite?.storeCfg?.userData?.PLP_DISPLAY_SKU_CATEGORIES;
      const isSkuCategory = Array.isArray(plpDisplaySkuCategories.split(","))
        ? categoryIdentifier !== "" && plpDisplaySkuCategories.includes(categoryIdentifier)
        : categoryIdentifier === plpDisplaySkuCategories;
      if (isSkuCategory) {
        Object.assign(query, {
          searchType: 101,
        });
      }
      if (
        mySite?.storeCfg?.userData?.PLP_FILTER_ATTRIBUTE &&
        mySite?.storeCfg?.userData?.PLP_FILTER_ATTRIBUTE.length > 0
      ) {
        Object.assign(query, {
          excludedAttribute: mySite?.storeCfg?.userData?.PLP_FILTER_ATTRIBUTE,
        });
      }
      Object.assign(parameters, { query });

      if (pageOffset >= 0) {
        Object.assign(query, { offset: pageOffset });
      }

      if (pageLimitWithoutPush >= 0) {
        Object.assign(query, { limit: pageLimit - genericPushWidget.count });
      }

      const facets = {};

      // Extract facets from `queryPage`
      for (const [key, value] of queryPage.entries()) {
        if (key && !isQueryParamAManualFilter(key) && isQueryParamAllowedAsFacet(key)) {
          facets[key] = value;
        }
      }
      // Decode keys to ensure single encoding
      const formattedFacets = Object.entries(facets).map(([key]) => `${key}`);

      // Add original facets to the query object in the required format
      if (Object.keys(facets).length > 0) {
        if (formattedFacets.length > 0) {
          Object.assign(query, { facet: formattedFacets });
        }
      }
      if (searchTerm === "*" || promotion) {
        Object.assign(query, {
          facet: [
            ...formattedFacets,
            mySite?.storeCfg?.userData?.ONSPECIAL_FACET_ID || "facets.7741124012283338835.value.raw%3A%221%22", // Add the new facet
          ],
        });
      }

      /* update minPrice and maxPrice parameters from URL */
      if (minPrice && maxPrice) {
        if (Number(minPrice) !== 0 && Number(maxPrice) !== 0) {
          Object.assign(query, {
            minPrice: minPrice,
            maxPrice: maxPrice,
          });
        }
      }

      const defaultValue = SORT_OPTIONS.getDefaultSortValue(
        isEmpty(searchTerm) ? SEARCH_PLP_PROFILE : SEARCH_FIND_PROFILE,
        mySite?.storeCfg?.userData?.SENSFUEL_RANKING_ENABLED === "true"
      );

      const orderBy = queryPage.get("sortOption") || defaultValue;

      if (orderBy?.length) {
        query.orderBy = orderBy;
      }

      //to update local states
      let selectedFacet: SelectedFacet = {
        selectedSortOption: query.orderBy,
        pageNum: page || 1,
      };
      if (promotion) {
        selectedFacet = {
          promotionEnabled: true,
          ...selectedFacet,
        };
      }
      if (Number(minPrice) !== 0 && Number(maxPrice) !== 0) {
        const priceFacet = {
          minPrice: Number(minPrice),
          maxPrice: Number(maxPrice),
        };
        selectedFacet = {
          ...selectedFacet,
          ...priceFacet,
        };
      }
      if (!selectedFacet?.selectedPageOffset && pageDefaultOffset !== undefined) {
        selectedFacet = {
          ...selectedFacet,
          selectedPageOffset: pageOffset,
        };
      }

      let finalPromoProductParameters = productParameters;
      if (productParameters) {
        finalPromoProductParameters = {
          ...productParameters,
          query: {
            ...query,
            ...productParameters?.query,
          },
        };
      }

      // Clear any stale snapshot on a fresh (non-back) page load so we
      // never accidentally restore it on a future navigation.
      clearSnapshot();

      dispatch(
        catalogActions.getProductListAction({
          parameters: promotionComponent ? finalPromoProductParameters : parameters,
          states: selectedFacet || {},
          removeCommonSubCategories: !promotionComponent,
        })
      );
      dispatch(setPageLimit(genericPushWidget.count));
    }

    return () => {
      const currentPathname = window.location.pathname;
      const isGoingToPDP =
        (_restoredForUrl !== "" && currentPathname === _restoredForUrl) ||
        sessionStorage.getItem(PLP_NAVIGATED_TO_PDP_KEY) === "1";

      if (!isGoingToPDP) {
        clearSnapshot();
        dispatch(
          catalogActions.resetProductListAction({
            parameters: {
              query: {
                profileName: isEmpty(searchTerm) ? SEARCH_PLP_PROFILE : SEARCH_FIND_PROFILE,
              },
            },
            removeCommonSubCategories: !promotionComponent,
          })
        );
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId, mySite, contract, genericPushWidget.loading]);

  const storedTotal = useSelector(productListTotalSelector);

  useEffect(() => {
    if (productListTotal < 0) return;
    if (storedTotal === productListTotal) return;

    dispatch(setProductListTotal(productListTotal));
  }, [productListTotal, storedTotal, dispatch]);

  /**
   * Removes selected facet and dispatches request to get product list
   * @param selection The selected facet entry's value
   */
  const onFacetRemove = (selection: string) => {
    // Any explicit user filter action invalidates the saved snapshot
    clearSnapshot();

    let newSelectedFacets = { ...selectedFacets };
    if (newSelectedFacets[selection] !== undefined) {
      const { [selection]: tempLabel, ...newRemovedSelectedFacets }: { [key: string]: string } = newSelectedFacets;
      newSelectedFacets = newRemovedSelectedFacets;
    }
    const currentPath = window.location.pathname;
    const params = new URLSearchParams(window.location.search);
    const promotionFacet = params.get("promotionFacet");
    let newStates: any = {
      selectedFacets: newSelectedFacets,
    };

    if (promotionFacet) {
      newStates = {
        ...newStates,
        promotionEnabled: true,
      };
    }
    dispatchGetProductListAction(
      newSelectedFacets,
      selectedFacetLimits,
      selectedMinPrice,
      selectedMaxPrice,
      pageDefaultOffset,
      selectedSortOption,
      newStates
    );
    // Step 3: Update the URL by removing the facet

    // Decode the parameter names and values before checking
    const decodedParams = new URLSearchParams();
    // Loop through each parameter and decode
    params.forEach((value, key) => {
      decodedParams.set(key, value);
    });
    // Step 4: Remove the selected facet from the decoded URL
    if (decodedParams.has(selection)) {
      decodedParams.delete(selection); // Remove the facet from URL if it exists
    }
    // Step 5: If no facets are selected, clear all facet-related parameters, including minPrice and maxPrice
    if (Object.keys(newSelectedFacets).length === 0) {
      // Collect all keys to delete, excluding minPrice and maxPrice
      const keysToDelete: string[] = [];
      decodedParams.forEach((_, key) => {
        if (key !== "sortOption" && key !== "minPrice" && key !== "maxPrice" && key !== "promotionFacet") {
          keysToDelete.push(key); // Collect keys to avoid modifying the map while iterating
        }
      });

      keysToDelete.forEach((key) => {
        console.log(`Removing key: ${key}`);
        decodedParams.delete(key); // Remove all keys except minPrice and maxPrice
      });
    }
    // Step 6: Build the new URL and push it to browser history
    const newUrl = `${currentPath}${decodedParams.toString() ? `?${decodedParams.toString()}` : ""}`;
    // Step 7: Update the browser URL
    window.history.pushState({}, "", newUrl);
  };

  /**
   * Removes all selected facet and dispatches request to get product list
   * @param event The event handler
   */
  const onClearAll = (event: MouseEvent<HTMLAnchorElement>) => {
    event?.preventDefault();
    // Any explicit user filter action invalidates the saved snapshot
    clearSnapshot();

    const newSelectedFacets = {};
    const newStates = {
      selectedFacets: newSelectedFacets,
      minPrice: -1,
      maxPrice: -1,
      promotionEnabled: false,
    };
    dispatchGetProductListAction(
      newSelectedFacets,
      selectedFacetLimits,
      -1,
      -1,
      pageDefaultOffset,
      selectedSortOption,
      newStates
    );
    // Step 1: Get the current URL and search params
    const currentPath = window.location.pathname;
    const params = new URLSearchParams(window.location.search);
    const sortOption = params.get("sortOption");
    // Step 2: Decode keys and remove all except 'sortOption', 'minPrice', and 'maxPrice'
    const keysToRemove: string[] = [];
    params.forEach((value, key) => {
      const decodedKey = decodeURIComponent(key);
      if (decodedKey !== "sortOption") {
        keysToRemove.push(key); // Collect keys to delete after iteration
      }
    });

    // Remove the collected keys
    keysToRemove.forEach((key) => {
      params.delete(key);
    });
    if (sortOption) {
      params.set("sortOption", sortOption);
    }
    const newUrl = `${currentPath}?${params.toString()}`;

    // Step 5: Update the URL without reloading the page
    window.history.pushState({}, "", newUrl);
  };

  /**
   * Sets the sort option to order the product list and dispatches request to get product list
   * @param event The event handler
   */
  const onSortOptionChange = (event: ChangeEvent<{ name?: string | undefined; value: unknown }>) => {
    // Sort change = user intent to see fresh results
    clearSnapshot();

    const selection = event.target.value as string;

    const currentPath = window.location.pathname;
    const params = new URLSearchParams(window.location.search);
    const promotion = params.get("promotionFacet") || promotionComponent;

    if (sortOptions?.[0].value === selection) {
      params.delete("sortOption"); // Remove sortOption if value default one for sort options
    } else {
      params.set("sortOption", selection);
    }
    params.delete("p");

    const newUrl = `${currentPath}?${params.toString()}`;
    window.history.pushState({}, "", newUrl);

    let newStates: any = {
      selectedSortOption: selection,
      //promotionEnabled: promotionFacet,
    };
    if (promotion) {
      newStates = {
        ...newStates,
        promotionEnabled: true,
      };
    }
    dispatchGetProductListAction(
      selectedFacets,
      selectedFacetLimits,
      selectedMinPrice,
      selectedMaxPrice,
      pageDefaultOffset,
      selection,
      newStates
    );
  };

  useEffect(() => {
    const handler = (e: any) => {
      const value = e.detail;

      const event = {
        target: { value },
      } as ChangeEvent<{ name?: string; value: unknown }>;

      onSortOptionChange(event);
    };

    window.addEventListener("plp-sort-change", handler);

    return () => {
      window.removeEventListener("plp-sort-change", handler);
    };
  }, [onSortOptionChange]);
  /**
   * Reset the selected price range and dispatches request to get product list
   */
  const clearPriceFacet = () => {
    clearSnapshot();

    const newStates = {
      minPrice: -1,
      maxPrice: -1,
    };
    dispatchGetProductListAction(
      selectedFacets,
      selectedFacetLimits,
      -1,
      -1,
      pageDefaultOffset,
      selectedSortOption,
      newStates
    );
    const currentPath = window.location.pathname;
    const params = new URLSearchParams(window.location.search);

    // Decode the parameter names and values before checking
    const decodedParams = new URLSearchParams();

    // Loop through each parameter and decode
    params.forEach((value, key) => {
      decodedParams.set(key, value);
    });

    // Step 2: Remove the minPrice and maxPrice,page parameters
    decodedParams.delete("minPrice");
    decodedParams.delete("maxPrice");
    decodedParams.delete("p");

    // Step 3: Ensure the sortOption is appended to the end of the URL
    const sortOption = decodedParams.get("sortOption");
    if (sortOption !== null && sortOptions?.[0]?.value !== sortOption) {
      decodedParams.delete("sortOption"); // Remove the sortOption temporarily
      decodedParams.append("sortOption", sortOption); // Re-add it at the end
    }
    // Step 4: Build the new URL and push it to browser history
    const newUrl = `${currentPath}?${decodedParams.toString()}`;
    // Step 5: Update the browser URL
    window.history.pushState({}, "", newUrl);
  };

  const clearPromotionalFacet = () => {
    clearSnapshot();

    const newStates = {
      selectedFacets: selectedFacets,
      promotionEnabled: false,
    };
    dispatchGetProductListAction(
      selectedFacets,
      selectedFacetLimits,
      selectedMinPrice,
      selectedMaxPrice,
      pageDefaultOffset,
      selectedSortOption,
      newStates
    );
    const currentPath = window.location.pathname;
    const params = new URLSearchParams(window.location.search);
    const sortOption = params.get("sortOption");
    // Set the promotionFacet parameter to the new value
    params.delete("promotionFacet");
    if (sortOption) {
      params.set("sortOption", sortOption);
    }
    params.delete("p");
    // Construct the new URL with the updated parameters
    const newUrl = `${currentPath}?${params.toString()}`;
    // Update the browser's URL without reloading the page
    window.history.pushState({ path: newUrl }, "", newUrl);
  };
  /**
   * Sets the page offset and dispatches request to get product list
   * @param value The page number
   */
  const onPageChange = (value: number, append = false, currentProductNumber: number) => {
    const params = new URLSearchParams(window.location.search);
    // Set the promotionFacet parameter to the new value
    // params.delete("promotionFacet");
    const sortOption = params.get("sortOption");
    const promotion = params.get("promotionFacet");
    if (sortOption) {
      params.set("sortOption", sortOption);
    }

    const pageNumber = value;

    const offset = append ? currentProductNumber : (pageNumber - 1) * pageLimit;

    dispatchGetProductListAction(
      selectedFacets,
      selectedFacetLimits,
      selectedMinPrice,
      selectedMaxPrice,
      offset,
      selectedSortOption,
      {
        selectedPageOffset: offset,
        appendProducts: append,
      }
    );
  };

  /**
   * Loads the previous page and prepends products to the current list
   * @param currentDisplayedPage The currently displayed first page number
   */
  const loadPreviousPage = (currentDisplayedPage: number) => {
    const prevPage = currentDisplayedPage - 1;
    const params = new URLSearchParams(window.location.search);
    const promotion = params.get("promotionFacet");

    // Update URL to reflect the new "first" page
    if (prevPage > 1) {
      params.set("p", String(prevPage));
    } else {
      params.delete("p");
    }
    const newUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ""}`;
    window.history.pushState({ path: newUrl }, "", newUrl);

    const offset = (prevPage - 1) * pageLimit;

    dispatchGetProductListAction(
      selectedFacets,
      selectedFacetLimits,
      selectedMinPrice,
      selectedMaxPrice,
      offset,
      selectedSortOption,
      {
        selectedPageOffset: offset,
        prependProducts: true,
        pageNum: prevPage,
        ...(promotion ? { promotionEnabled: true } : {}),
      }
    );
  };

  /**
   * Wrapper to dispatch request to get product list
   * @param selectedFacets The updated selected facets map
   * @param selectedFacetLimits The updated selected facet limits array
   * @param selectedMinPrice The selected min price or -1, if not set
   * @param selectedMaxPrice The selected max price or -1, if not set
   * @param selectedPageOffset The selected offset for pagination
   * @param selectedSortOption The selected sort option
   * @param states The states that have changed and should be persisted back to redux
   */
  const dispatchGetProductListAction = (
    selectedFacets: any,
    selectedFacetLimits: string[],
    selectedMinPrice: number,
    selectedMaxPrice: number,
    selectedPageOffset: number,
    selectedSortOption: string,
    states: any
  ) => {
    const { widget, cancelToken, ...query } = paramsBase;
    const params = new URLSearchParams(window.location.search);
    Object.assign(query, {
      profileName: isEmpty(query.searchTerm) ? SEARCH_PLP_PROFILE : SEARCH_FIND_PROFILE,
    });
    Object.assign(query, {
      customerSegment: GENERIC_CUSTOMER,
    });
    const plpDisplaySkuCategories = mySite?.storeCfg?.userData?.PLP_DISPLAY_SKU_CATEGORIES;
    const isSkuCategory = Array.isArray(plpDisplaySkuCategories.split(","))
      ? categoryIdentifier !== "" && plpDisplaySkuCategories.includes(categoryIdentifier)
      : categoryIdentifier === plpDisplaySkuCategories;
    if (isSkuCategory) {
      Object.assign(query, {
        searchType: 101,
      });
    }
    // if (searchTerm === "" && genericPushWidget?.loading === true) {
    //   Object.assign(query, { limit: pageLimit - genericPushWidget.count });
    // }
    if (states?.limit) {
      Object.assign(query, {
        limit: states.limit,
      });
    } else if (searchTerm === "" && genericPushWidget?.loading === true && !states?.appendProducts) {
      Object.assign(query, {
        limit: pageLimit - genericPushWidget.count,
      });
    } else {
      Object.assign(query, {
        limit: pageLimit,
      });
    }
    const selectedFacetsArray = Object.keys(selectedFacets);
    if (selectedFacetsArray.length > 0) {
      Object.assign(query, { facet: selectedFacetsArray });
    }
    if (selectedFacetLimits.length > 0) {
      Object.assign(query, { facetLimit: selectedFacetLimits });
    }
    if (selectedMinPrice > -1 && selectedMaxPrice > -1) {
      Object.assign(query, { minPrice: selectedMinPrice, maxPrice: selectedMaxPrice });
    }
    if (selectedPageOffset >= 0) {
      Object.assign(query, { offset: selectedPageOffset });
    }

    const orderBy = params.get("sortOption") || selectedSortOption;

    if (orderBy && orderBy.length > 0) {
      Object.assign(query, { orderBy });
    }

    if (
      mySite?.storeCfg?.userData?.PLP_FILTER_ATTRIBUTE &&
      mySite?.storeCfg?.userData?.PLP_FILTER_ATTRIBUTE.length > 0
    ) {
      Object.assign(query, {
        excludedAttribute: mySite?.storeCfg?.userData?.PLP_FILTER_ATTRIBUTE,
      });
    }

    const parameters: Record<any, any> = { widget, cancelToken, query, identifier: categoryIdentifier };

    if (searchTerm === "*" || states.promotionEnabled) {
      Object.assign(query, {
        facet: [
          ...selectedFacetsArray, // Include existing facets (if any)
          mySite?.storeCfg?.userData?.ONSPECIAL_FACET_ID, // Add the new facet
        ],
      });
      parameters.searchTerm = "*"; //need this line for catalog.ts worker file promo fields.
    }

    const pageLimitWithoutPush = pageLimit - genericPushWidget.count;
    const pageNum = Math.floor(selectedPageOffset / pageLimitWithoutPush) + 1;

    const newStates = { ...states, pageNum };
    if (newStates["selectedPageOffset"] === undefined) {
      newStates["selectedPageOffset"] = pageDefaultOffset;
    }
    dispatch(
      catalogActions.getProductListAction({
        parameters,
        states: newStates,
        removeCommonSubCategories: !promotionComponent,
        //selectedSellers,
      })
    );
    if (!states?.appendProducts) {
      scrollToTop();
    }
  };

  const isValidUrl = (productList: any): boolean => {
    return productList?.length && productList[0].seo?.href;
  };

  return {
    onSortOptionChange,
    clearPriceFacet,
    clearPromotionalFacet,
    onPageChange,
    isValidUrl,
    onClearAll,
    onFacetRemove,
    loadPreviousPage,
    cancels,
    paramsBase,
    sortOptions,
    selectedSortOption,
    defaultCurrencyID,
    categoryId,
    searchTerm,
    selectedMinPrice,
    selectedMaxPrice,
    pageLimit: pageLimit - genericPushWidget.count,
    selectedFacets,
    priceSelected,
    promotionFacetSelected,
    productList,
    selectedPageOffset,
    selectedPage,
    productListTotal,
    suggestedKeywords,
    selectFacetRemove,
    genericPushWidget,
    isRestoredFromPDP: snapshotRestored.current,
  };
};
